/* eslint-disable @typescript-eslint/restrict-plus-operands */
import classNames from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import useRouterStatus from 'src/common/hooks/useRouterStatus'

type HeaderProps = {
    title: string
    isNavigationPanelOpen: boolean
    openNavPanel: () => void
    isLoggedIn: boolean
}

type BreadCrumb = {
    breadcrumb: string
    href: string
}

const convertBreadcrumb = (string: string) => {
    return string.replace(/-/g, ' ')
}

const OpenNavIconClasses = classNames(
    'fas',
    'fa-times',
    'text-yf-purple',
    'w-6',
    'h-6',
    'text-2xl',
    'leading-none'
)
const ClosedNavIconClasses = classNames(
    'fas',
    'fa-bars',
    'text-yf-purple',
    'w-6',
    'h-6',
    'text-2xl',
    'leading-none'
)

export const Header = ({
    title,
    isNavigationPanelOpen,
    openNavPanel,
    isLoggedIn
}: HeaderProps) => {
    const { isLoading } = useRouterStatus()
    const router = useRouter()
    const [breadcrumbs, setBreadcrumbs] = useState<BreadCrumb[]>(null)

    useEffect(() => {
        if (router) {
            const linkPath = router.asPath.replace(/\?.*$/, ' ').split('/')
            linkPath.shift()

            const pathArray = linkPath.map((path, i) => {
                return {
                    breadcrumb: path,
                    href: '/' + linkPath.slice(0, i + 1).join('/')
                }
            })

            setBreadcrumbs(pathArray)
        }
    }, [router])

    return (
        <div className="flex px-4 py-2 md:py-4 bg-white items-center overflow-x-hidden drop-shadow">
            {!isLoggedIn && (
                <div className="order-4 hidden md:block">
                    <button
                        onClick={() =>
                            void router.push(
                                `/login${
                                    breadcrumbs.length > 0 - 1
                                        ? `?redirect=${
                                              breadcrumbs[breadcrumbs.length - 1].href
                                          }`
                                        : ''
                                }`
                            )
                        }
                        className="border border-yf-purple rounded px-10 py-2 text-yf-purple hover:bg-yf-purple hover:text-white transition-all cursor-pointer"
                    >
                        Sign In
                    </button>
                    <button
                        onClick={() =>
                            void router.push(
                                `/register${
                                    breadcrumbs.length > 0 - 1
                                        ? `?redirect=${
                                              breadcrumbs[breadcrumbs.length - 1].href
                                          }`
                                        : ''
                                }`
                            )
                        }
                        className="border border-yf-green bg-yf-green rounded px-10 py-2 text-white ml-2 hover:bg-white hover:text-yf-green transition-all cursor-pointer"
                    >
                        Join for Free
                    </button>
                </div>
            )}
            <div className={`order-1 ml-2 mr-2`}>
                <div
                    onClick={openNavPanel}
                    className="flex items-center justify-center cursor-pointer h-full z-50 lg:mr-6"
                >
                    <i
                        aria-hidden
                        className={classNames({
                            [OpenNavIconClasses]: isNavigationPanelOpen,
                            [ClosedNavIconClasses]: !isNavigationPanelOpen
                        })}
                    ></i>
                </div>
            </div>

            <div className="md:mr-4 max-h-12 items-center justify-center order-2 flex-shrink-0">
                <Link href="/">
                    <a>
                        <img
                            className="w-12 h-12 flex-grow-0 flex-shrink-0 cursor-pointer"
                            width="48px"
                            height="48px"
                            src="/logo.svg"
                            alt="Yes.Fit logo"
                        />
                    </a>
                </Link>
            </div>

            <div className="flex justify-center flex-col mr-auto hidden md:order-2 md:block">
                <h2 className="text-base leading-5 md:text-2xl text-black font-medium line-clamp-2">
                    {isLoading ? 'Loading...' : title}
                </h2>

                <div className="smMd:flex hidden">
                    <div className="inline-flex items-center justify-center mr-1">
                        <i
                            aria-hidden
                            className="fas fa-home w-4 h-4 text-base text-gray-500 leading-none mr-1"
                        ></i>
                    </div>

                    <Link href={`/${isLoggedIn ? 'dashboard-new' : ''}`}>
                        <a className="text-yf-green no-underline cursor-pointer capitalize mr-1">
                            <p className="text-base font-normal">Home</p>
                        </a>
                    </Link>

                    <div className="text-gray-300 text-base font-normal mr-1">/</div>

                    {breadcrumbs?.map((breadcrumb: BreadCrumb, i: number) => {
                        if (!breadcrumb.breadcrumb.length)
                            return <React.Fragment key={i}></React.Fragment>

                        return (
                            <React.Fragment key={i}>
                                <Link href={breadcrumb.href} passHref>
                                    <a
                                        className={`${
                                            i === breadcrumbs.length - 1
                                                ? 'text-gray-600'
                                                : 'text-yf-green'
                                        } no-underline cursor-pointer capitalize mr-1`}
                                    >
                                        <p
                                            className={`text-base ${
                                                i === breadcrumbs.length - 1
                                                    ? 'font-bold'
                                                    : 'font-normal'
                                            }`}
                                        >
                                            {convertBreadcrumb(breadcrumb.breadcrumb)}
                                        </p>
                                    </a>
                                </Link>

                                <div className="text-gray-300 text-base font-normal mr-1">
                                    /
                                </div>
                            </React.Fragment>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
